import { Bell, Menu, Search } from 'lucide-react';
import { Button, Input } from '@safe-minds-ai/ui';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type TopbarProps = {
  onToggleSidebar: () => void;
};

export function Topbar({ onToggleSidebar }: TopbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') ?? '';
    setSearchValue(query);
  }, [location.search]);

  const handleSubmit = () => {
    const trimmed = searchValue.trim();
    const targetBase = location.pathname.startsWith('/flags') ? '/flags' : '/users';
    if (!trimmed) {
      navigate(targetBase);
      return;
    }
    navigate(`${targetBase}?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-slate-200/60 bg-white/70 px-4 py-3 shadow-[0_20px_60px_-45px_rgba(24,60,165,0.25)] backdrop-blur-xl dark:border-slate-700/50 dark:bg-slate-900/80">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          className="h-10 w-10 rounded-full bg-white/80 text-slate-600 shadow-sm hover:bg-white lg:hidden dark:bg-slate-800/90 dark:text-slate-100 dark:hover:bg-slate-700/80"
          onClick={onToggleSidebar}
          aria-label="Open sidebar"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <div className="relative hidden items-center md:flex">
          <Search className="absolute left-3 h-4 w-4 text-slate-400 dark:text-slate-300" />
          <Input
            placeholder="Search..."
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSubmit();
              }
            }}
            className="h-10 w-[220px] rounded-full border border-slate-200/70 bg-white/80 pl-10 text-sm text-slate-700 shadow-inner placeholder:text-slate-400 md:w-[320px] dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-100 dark:placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:border-blue-400"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative">
          <Button
            variant="ghost"
            className="h-10 w-10 rounded-full bg-white/80 text-slate-600 shadow-sm hover:bg-white dark:bg-slate-800/90 dark:text-slate-100 dark:hover:bg-slate-700/80"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
          </Button>
          <span className="absolute right-1.5 top-1.5 h-3 w-3 rounded-full bg-rose-500 ring-2 ring-slate-900/70" />
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white/80 text-sm font-semibold text-slate-600 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/90 dark:text-slate-100">
          SA
        </div>
      </div>
    </div>
  );
}
